import { useCallback, useEffect, useRef, useState } from "react";
import { calibrationService, DEFAULT_GROUP_KEY } from "../../../../services/CalibrationService";
import { normalizeCalibrationDttm } from "../util";
import { ROOT_GEOJSON_DATASET_TYPE, STRUCTURE_MILES_DATASET_TYPE } from "./structureMilesUtils";

/**
 * Load a project's structure-miles dataset, and the latest root-geojson version alongside it.
 *
 * The card owns its data through this hook rather than having SensorCounts lift it, so a
 * structure-miles read failure cannot blank the sensor-count chart or disturb the
 * "Missing datasets:" line. That isolation is structural, not defensive.
 *
 * The root-geojson version is resolved eagerly, in the same round trip as the dataset, rather
 * than lazily inside the Calculate handler. Doing it up front is what makes three things
 * possible: the button's disabled state is honest, the card can name the root-geojson it would
 * read before the user commits, and the staleness check exists at all. The cost is one extra
 * listing GET per client/project change.
 *
 * `loadError` and "no dataset" are deliberately distinct: a transient read failure must never
 * make the card assert the dataset is absent and offer to recompute something that already
 * exists.
 */
export default function useStructureMiles(client, project) {
    const [dataset, setDataset] = useState(null);
    const [meta, setMeta] = useState(null);
    const [latestRootGeojsonVersion, setLatestRootGeojsonVersion] = useState(null);
    // Seeded from the props rather than false: effects run after paint, so mounting with a
    // client and project already selected would otherwise commit one frame asserting both
    // "no structure miles yet" and "no root-geojson found" before the first fetch starts.
    const [loading, setLoading] = useState(Boolean(client && project));
    const [loadError, setLoadError] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);

    // Lets pollForNewerDataset notice that the user switched project while it was looping.
    //
    // Deliberately NOT paired with an isMounted ref. useRef values survive React Fast Refresh while
    // effect cleanups still run, so a `useEffect(() => () => { mounted.current = false }, [])` flag
    // latches to false on the first hot reload and is never re-armed -- which silently made every
    // poll bail on its first iteration. React 18 makes a setState on an unmounted component a
    // harmless no-op, so the flag bought nothing and cost that bug.
    const selectionRef = useRef(`${client}|${project}`);
    useEffect(() => {
        selectionRef.current = `${client}|${project}`;
    }, [client, project]);

    useEffect(() => {
        if (!client || !project) {
            setDataset(null);
            setMeta(null);
            setLatestRootGeojsonVersion(null);
            setLoadError(null);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setLoadError(null);

        (async () => {
            // The dataset read is wrapped into an outcome object instead of sharing a catch, so
            // an absent dataset (resolves null) stays distinguishable from a failed read.
            const [milesOutcome, rootGeojsonVersion] = await Promise.all([
                calibrationService
                    .getLatestDatasetViaSignedUrl(
                        client,
                        project,
                        DEFAULT_GROUP_KEY,
                        STRUCTURE_MILES_DATASET_TYPE,
                        false
                    )
                    .then((response) => ({ response, error: null }))
                    .catch((error) => ({ response: null, error })),
                calibrationService.getLatestVersionDttm(client, project, DEFAULT_GROUP_KEY, ROOT_GEOJSON_DATASET_TYPE),
            ]);

            if (cancelled) return;

            setLatestRootGeojsonVersion(rootGeojsonVersion);
            if (milesOutcome.error) {
                setLoadError(milesOutcome.error);
                setDataset(null);
                setMeta(null);
            } else {
                setLoadError(null);
                // Deliberately not cleared before the fetch: on a refresh the existing numbers stay
                // on screen so an in-place reload cannot collapse the card and yank the controls
                // below it down the sidebar.
                setDataset(milesOutcome.response?.data ?? null);
                setMeta(milesOutcome.response?.meta ?? null);
            }
            setLoading(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [client, project, reloadToken]);

    // Bumping the token re-runs the same guarded effect, so a manual refresh inherits the
    // cancellation handling for free rather than needing its own request-id tracking.
    const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

    /**
     * Watch for a structure-miles version newer than `baselineVersion`, and load it if one appears.
     *
     * Used after a kickoff. The route computes synchronously inside the API lambda and in practice
     * returns in well under a second, so a short poll turns "click, then go press refresh" into
     * "click, numbers appear". Resolves true if a new version landed and was loaded, false if the
     * window expired -- in which case the caller falls back to telling the user to check the
     * Progress Viewer, which is the correct answer for a project big enough to outlast the window.
     *
     * Only the version listing is polled, not the payload, so each tick is one cheap request.
     * Abandons itself if the component unmounts or the selection changes mid-loop.
     */
    const pollForNewerDataset = useCallback(
        async (baselineVersion, { timeoutMs = 25000, intervalMs = 1000, shouldAbort = () => false } = {}) => {
            const selection = `${client}|${project}`;
            const baseline = normalizeCalibrationDttm(baselineVersion) ?? null;
            const deadline = Date.now() + timeoutMs;
            const stillRelevant = () => selectionRef.current === selection && !shouldAbort();

            while (Date.now() < deadline) {
                if (!stillRelevant()) return false;
                const latest = await calibrationService.getLatestVersionDttm(
                    client,
                    project,
                    DEFAULT_GROUP_KEY,
                    STRUCTURE_MILES_DATASET_TYPE
                );
                if (!stillRelevant()) return false;
                if (latest && normalizeCalibrationDttm(latest) !== baseline) {
                    setReloadToken((token) => token + 1);
                    return true;
                }
                await new Promise((resolve) => setTimeout(resolve, intervalMs));
            }
            return false;
        },
        [client, project]
    );

    return { dataset, meta, latestRootGeojsonVersion, loading, loadError, refresh, pollForNewerDataset };
}
